import { DeesElement, customElement, property, state, html, css, cssManager } from '@design.estate/dees-element';
import type { Editor } from '@tiptap/core';
import { DeesServiceLibLoader } from '../../../services/DeesServiceLibLoader.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesInputDropdownPopup } from '../dees-input-dropdown/dees-input-dropdown-popup.js';
import type { IInputReference, TReferenceContent, TReferenceSearch } from './interfaces.js';
import { referenceContent, referenceDocument } from './document.js';
import { demoFunc } from './dees-input-reference.demo.js';

declare global { interface HTMLElementTagNameMap { 'dees-input-reference': DeesInputReference; } }

@customElement('dees-input-reference')
export class DeesInputReference extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Input', 'Kanban'];
  @property({ attribute: false }) accessor value: TReferenceContent = [];
  @property({ attribute: false }) accessor searchReferences: TReferenceSearch | undefined;
  @property({ type: String }) accessor trigger = '#_';
  @property({ type: String }) accessor label = 'Message';
  @property({ type: String }) accessor placeholder = 'Write a comment…';
  @property({ type: String }) accessor searchLabel = 'Search tickets';
  /** Changing documents resets editor history, so undo cannot cross conversation boundaries. */
  @property({ type: String }) accessor documentKey = '';
  @property({ type: Boolean }) accessor disabled = false;
  @state() private accessor loadError = '';
  private editor?: Editor;
  private initializing?: Promise<void>;
  private connection = 0;
  private popup?: DeesInputDropdownPopup;
  private popupOpening?: Promise<void>;
  private referenceRange?: { from: number; to: number };
  private serializedValue = '';

  public static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
    :host { display: block; min-width: 0; color: var(--dees-color-text-primary); font-family: var(--dees-font-family); }
    .editor { border: 1px solid var(--dees-color-border-default); border-radius: var(--dees-radius-lg); background: var(--dees-color-bg-primary); }
    .editor:focus-within { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 1px; }
    .ProseMirror { padding: 12px; min-height: var(--dees-reference-min-height, 84px); max-height: 260px; overflow: auto; outline: none; white-space: pre-wrap; overflow-wrap: anywhere; font-size: var(--dees-reference-font-size, var(--dees-font-body-size)); line-height: 1.6; }
    .ProseMirror p { margin: 0; }
    .ProseMirror p + p { margin-top: 0; }
    .ProseMirror:has(> p:only-child > br:only-child)::before { content: attr(data-placeholder); float: left; color: var(--dees-color-text-muted); pointer-events: none; height: 0; }
    .ProseMirror [data-reference-id] { display: inline; padding: 1px 5px; border-radius: var(--dees-radius-sm); color: var(--dees-color-link); background: var(--dees-color-fill-tertiary); cursor: pointer; font-weight: 500; }
    .ProseMirror [data-reference-id]:focus-visible, .ProseMirror .ProseMirror-selectednode { outline: 2px solid var(--dees-color-focus-ring); }
    .ProseMirror[contenteditable=false] { opacity: .65; }
    .error { color: var(--dees-color-text-error); font-size: var(--dees-font-footnote-size); }
  `];

  public render() {
    return html`<div class="editor" part="editor"></div>${this.loadError ? html`<p class="error" role="alert">${this.loadError}</p>` : ''}`;
  }

  public async connectedCallback(): Promise<void> {
    const connection = super.connectedCallback();
    this.connection++;
    void this.initialize();
    await connection;
  }

  /** Resolves after the lazy editor is mounted; also useful when focusing after opening a modal. */
  public async ready(): Promise<void> {
    await this.initialize();
    if (!this.editor) throw new Error(this.loadError || 'Connect the reference editor before calling ready().');
  }

  private initialize(): Promise<void> {
    if (this.editor) return Promise.resolve();
    if (this.initializing) return this.initializing;
    const connection = this.connection;
    const operation = (async () => {
      await this.updateComplete;
      const { Editor, Node, StarterKit } = await DeesServiceLibLoader.getInstance().loadTiptap();
      if (!this.isConnected || connection !== this.connection) return;
      const referenceNode = Node.create({
        name: 'catalogReference', group: 'inline', inline: true, atom: true, selectable: true,
        addAttributes: () => ({
          id: { default: '', parseHTML: element => element.getAttribute('data-reference-id') },
          label: { default: '', parseHTML: element => element.textContent },
        }),
        parseHTML: () => [{ tag: 'span[data-reference-id]' }],
        renderHTML: ({ node }) => ['span', { 'data-reference-id': String(node.attrs.id), contenteditable: 'false', role: 'link', tabindex: '0' }, String(node.attrs.label)],
        renderText: ({ node }) => String(node.attrs.label),
      });
      this.editor = new Editor({
        element: this.shadowRoot!.querySelector<HTMLElement>('.editor')!,
        injectCSS: false,
        editable: !this.disabled,
        extensions: [StarterKit.configure({ blockquote: false, bold: false, bulletList: false, code: false, codeBlock: false, heading: false, horizontalRule: false, italic: false, listItem: false, orderedList: false, strike: false }), referenceNode],
        content: referenceDocument(this.value),
        editorProps: {
          attributes: { role: 'textbox', 'aria-multiline': 'true', 'aria-label': this.label, 'data-placeholder': this.placeholder },
          handleClickOn: (_view, _position, node, _nodePosition, event, direct) => {
            if (!direct || node.type.name !== 'catalogReference') return false;
            event.preventDefault(); this.openReference({ id: String(node.attrs.id), label: String(node.attrs.label) }); return true;
          },
          handleDOMEvents: {
            keydown: (_view, event) => {
              const target = event.target as HTMLElement;
              if (event.key === 'Enter' && target.dataset.referenceId) {
                event.preventDefault(); this.openReference({ id: target.dataset.referenceId, label: target.textContent ?? '' }); return true;
              }
              if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && !event.isComposing && !this.disabled) {
                event.preventDefault(); this.dispatchEvent(new CustomEvent('reference-submit', { bubbles: true, composed: true })); return true;
              }
              return false;
            },
            compositionend: () => { queueMicrotask(() => this.detectTrigger()); return false; },
          },
        },
        onUpdate: ({ editor, transaction }) => {
          this.value = referenceContent(editor.getJSON());
          this.serializedValue = JSON.stringify(this.value);
          this.dispatchEvent(new CustomEvent('reference-input', { detail: { value: this.value }, bubbles: true, composed: true }));
          if (!transaction.getMeta('preventReferenceTrigger')) this.detectTrigger();
        },
      });
      this.serializedValue = JSON.stringify(this.value);
      this.loadError = '';
    })().catch(error => { this.loadError = error instanceof Error ? error.message : 'Could not load the editor.'; })
      .finally(() => { if (this.initializing === operation) this.initializing = undefined; });
    this.initializing = operation;
    return operation;
  }

  public updated(changed: Map<string, unknown>): void {
    if (changed.has('documentKey') && changed.get('documentKey') !== undefined && this.editor) {
      this.connection++;
      void this.closePicker(false);
      this.editor.destroy(); this.editor = undefined; this.initializing = undefined;
      void this.initialize();
    }
    if (changed.has('disabled')) {
      this.editor?.setEditable(!this.disabled, false);
      if (this.disabled) void this.closePicker(false);
    }
    if (changed.has('value') && this.editor && JSON.stringify(this.value) !== this.serializedValue) {
      void this.closePicker(false);
      this.editor.commands.setContent(referenceDocument(this.value), false);
      this.serializedValue = JSON.stringify(this.value);
    }
    if (changed.has('label') || changed.has('placeholder')) this.editor?.setOptions({ editorProps: {
      ...this.editor.options.editorProps,
      attributes: { role: 'textbox', 'aria-multiline': 'true', 'aria-label': this.label, 'data-placeholder': this.placeholder },
    } });
  }

  public focus(options?: FocusOptions): void { this.editor?.view.dom.focus(options); }

  private detectTrigger(): void {
    const editor = this.editor;
    if (!editor || this.disabled || this.popup || !this.searchReferences || !this.trigger || editor.view.composing || !editor.isFocused) return;
    const { from, to, $from } = editor.state.selection;
    if (from !== to || $from.parentOffset < this.trigger.length) return;
    if ($from.parent.textBetween($from.parentOffset - this.trigger.length, $from.parentOffset, '', '\ufffc') !== this.trigger) return;
    this.referenceRange = { from: from - this.trigger.length, to };
    void this.openPicker();
  }

  private positionPicker(): void {
    if (!this.editor || !this.popup || !this.referenceRange) return;
    const coords = this.editor.view.coordsAtPos(this.referenceRange.to);
    this.popup.triggerRect = new DOMRect(coords.left, coords.top, 340, coords.bottom - coords.top);
    this.popup.opensToTop = window.innerHeight - coords.bottom < 300 && coords.top > window.innerHeight - coords.bottom;
  }

  private async openPicker(): Promise<void> {
    const popup = this.popup = new DeesInputDropdownPopup();
    popup.ownerComponent = this;
    popup.searchLabel = this.searchLabel;
    popup.searchOptions = async (query, signal) => (await this.searchReferences!(query, signal))
      .map(reference => ({ key: reference.id, option: reference.label, payload: reference }));
    this.positionPicker();
    popup.addEventListener('reposition-request', () => this.positionPicker());
    popup.addEventListener('close-request', () => { void this.closePicker(true); });
    popup.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Tab') { event.preventDefault(); void this.closePicker(true); }
    });
    popup.addEventListener('option-selected', (event: Event) => {
      const reference = (event as CustomEvent<{ payload: IInputReference }>).detail.payload;
      const range = this.referenceRange;
      if (range && this.editor && !this.disabled) {
        this.editor.chain().setMeta('preventReferenceTrigger', true).insertContentAt(range, [
          { type: 'catalogReference', attrs: reference }, { type: 'text', text: ' ' },
        ]).run();
      }
      void this.closePicker(true);
    });
    this.popupOpening = popup.show();
    try {
      await this.popupOpening;
      if (this.popup === popup && this.isConnected && !this.disabled) await popup.focusSearchInput();
    } catch (error) {
      this.loadError = error instanceof Error ? error.message : 'Could not open search.';
      void this.closePicker(false);
    }
  }

  private async closePicker(restoreFocus: boolean): Promise<void> {
    const popup = this.popup;
    const editor = this.editor;
    const opening = this.popupOpening;
    this.popup = undefined;
    this.referenceRange = undefined;
    if (!popup) return;
    await opening?.catch(() => undefined);
    await popup.hide();
    if (restoreFocus && this.isConnected && !this.disabled && this.editor === editor) editor?.commands.focus();
  }

  private openReference(reference: IInputReference): void {
    this.dispatchEvent(new CustomEvent('reference-open', { detail: reference, bubbles: true, composed: true }));
  }

  public async disconnectedCallback(): Promise<void> {
    this.connection++;
    void this.closePicker(false);
    this.editor?.destroy();
    this.editor = undefined;
    this.initializing = undefined;
    await super.disconnectedCallback();
  }
}
